/**
 * tus-js-client wrapper for resumable chunk uploads.
 *
 * Each 10-second recording chunk is uploaded as a separate tus upload.
 * tus handles transport-level retry/resume; the server-side stitching
 * pipeline (S3 event → DynamoDB chunk-sessions → FFmpeg) remains unchanged.
 */

import * as tus from 'tus-js-client'
import { getAccessToken } from '../authToken'
import { getTusEndpoint } from '../apiRouting'

export interface TusChunkUploadParams {
  sessionId: string
  chunkIndex: number
  blob: Blob
  mimeType: string
  totalChunks: number
  resumeUrl?: string | null
  onProgress?: (bytesUploaded: number, bytesTotal: number) => void
}

export interface TusUploadResult {
  success: boolean
  uploadUrl: string | null
  error?: Error
}

export function uploadChunkViaTus(
  params: TusChunkUploadParams,
): Promise<TusUploadResult> {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(params.blob, {
      endpoint: getTusEndpoint(),
      retryDelays: [0, 1000, 3000, 5000, 10000],
      chunkSize: Infinity, // each recording chunk is small (~100-500KB), no sub-chunking
      metadata: {
        sessionId: params.sessionId,
        chunkIndex: String(params.chunkIndex),
        totalChunks: String(params.totalChunks),
        mimeType: params.mimeType,
      },
      headers: {},
      uploadUrl: params.resumeUrl ?? undefined,

      onBeforeRequest: async (req) => {
        const token = await getAccessToken()
        if (token) {
          req.setHeader('Authorization', `Bearer ${token}`)
        }
      },

      onProgress: (bytesUploaded, bytesTotal) => {
        params.onProgress?.(bytesUploaded, bytesTotal)
      },

      onSuccess: () => {
        resolve({
          success: true,
          uploadUrl: upload.url ?? null,
        })
      },

      onError: (error) => {
        reject({
          success: false,
          uploadUrl: upload.url ?? null,
          error: error instanceof Error ? error : new Error(String(error)),
        })
      },
    })

    upload.start()
  })
}
